import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { message } from "antd";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import BasicModalFormComponent from "../../../components/formComponent/BasicModalFormComponent";
import FormTextField from "../../../components/general/FormTextField";
import FormSelect from "../../../components/general/FormSelect";
import FormCheckbox from "../../../components/general/FormCheckbox";
import { postRequest } from "../../../services/mainApp.service";

const initialFormValues = {
  CategoryId: null,
  CategoryName: "",
  DepartmentId: null,
  IsEnable: true,
};

const initialSearchValues = {
  CategoryId: null,
  SearchText: "",
};

const columns = [
  {
    title: "Category Name",
    dataIndex: "CategoryName",
    key: "CategoryName",
  },
  {
    title: "Department",
    dataIndex: "DepartmentName",
    key: "DepartmentName",
  },
];

const InventoryCategory = () => {
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);

  const [formFields, setFormFields] = useState(initialFormValues);
  const [searchFields, setSearchFields] = useState(initialSearchValues);
  const [itemList, setItemList] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [updateId, setUpdateId] = useState(null);
  const [shouldCloseModal, setShouldCloseModal] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = (searchParams = {}, pageNumber = 1, pageSize = 10) => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      CategoryId: null,
      SearchText: searchParams.SearchText || "",
      PageNumber: pageNumber,
      PageSize: pageSize,
    };

    postRequest("/Inv_Category", payload, controller)
      .then((response) => {

        setTableLoading(false);
        if (response.error === true) {
          message.error(response.errorMessage);
          return;
        }
        if (response.data.response === false) {
          message.error(response.data.DataSet?.Table?.errorMessage || "Error fetching data");
          return;
        }

        const dataSet = response.data;
        setItemList(dataSet.Data || []);

        // Update pagination with total count from API response
        // Assuming the API returns TotalCount or TotalRecords in the response
        const totalCount = dataSet?.TotalCount || dataSet?.TotalRecords || dataSet?.Data?.length || 0;
        setPagination((prev) => ({
          ...prev,
          current: pageNumber,
          pageSize: pageSize,
          total: totalCount,
        }));

        // Set supporting tables if available (for departments dropdown)
        if (dataSet.AdditionalData) {
          setSupportingTable({ AdditionalData: dataSet.AdditionalData });
        }
      })
      .catch((error) => {
        setTableLoading(false);
        if (error.message !== "canceled") {
          console.error("Error fetching categories:", error);
          message.error("Failed to fetch categories");
        }
      });
  };

  useEffect(() => {
    fetchData({}, pagination.current, pagination.pageSize);

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (updateId !== null) {
      const selectedItem = itemList.find((item) => item.CategoryId === updateId);
      if (selectedItem) {
        setFormFields({
          CategoryId: selectedItem.CategoryId,
          CategoryName: selectedItem.CategoryName || "",
          DepartmentId: selectedItem.DepartmentId || null,
          IsEnable: selectedItem.IsEnable !== undefined ? selectedItem.IsEnable : true,
        });
      }
    }
    setUpdateId(null);
  }, [updateId, itemList]);

  const handleSearchChange = (data) => {
    setSearchFields((prev) => ({
      ...prev,
      [data.name]: data.value,
    }));
  };

  const handleFormChange = (data) => {
    setFormFields((prev) => ({
      ...prev,
      [data.name]: data.value,
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmedSearch = {
      ...searchFields,
      SearchText: searchFields.SearchText?.trim() || "",
    };
    setSearchFields(trimmedSearch);
    // Reset to first page when searching
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(trimmedSearch, 1, pagination.pageSize);
  };

  const handleDeleteRow = (id) => {
    const payload = {
      OperationId: 4,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      CategoryId: id,
    };

    postRequest("/Inv_Category", payload, controller)
      .then((response) => {
        if (response.error === true) {
          message.error(response.errorMessage);
          return;
        }
        if (response.data.response === false) {
          message.error(response.data.DataSet?.Table?.errorMessage || "Error deleting category");
          return;
        }

        message.success(response.successMessage || "Category deleted successfully");
        fetchData(searchFields, pagination.current, pagination.pageSize);
      })
      .catch((error) => {
        if (error.message !== "canceled") {
          console.error("Error deleting category:", error);
          message.error("Failed to delete category");
        }
      });
  };

  const handleFormSubmit = (e, operationId) => {

    e.preventDefault();
    setFormLoading(true);

    const payload = {
      OperationId: operationId,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      CategoryId: formFields.CategoryId,
      CategoryName: formFields.CategoryName,
      DepartmentId: formFields.DepartmentId,
      IsEnable: formFields.IsEnable,
    };

    postRequest("/Inv_Category", payload, controller)
      .then((response) => {
        setFormLoading(false);

        const dataSet = response?.data;

        if (response.error === true) {
          message.error(response.errorMessage || response?.data?.Error_Message);
          return;
        }
        if (response?.data?.HasError === true) {
          message.error(response?.data?.Error_Message || "Error saving category");
          return;
        }

        message.success(response?.data?.Message || "Category saved successfully");

        setItemList(dataSet?.Data || []);

        // Set supporting tables if available (for departments dropdown)
        if (dataSet?.AdditionalData && Object.keys(dataSet.AdditionalData).length > 0) {
          setSupportingTable({ AdditionalData: dataSet.AdditionalData });
        }

        setFormFields(initialFormValues);
        setUpdateId(null);

        // Close modal if operationId is 3 (update) and response is successful
        if (operationId === 3) {
          setShouldCloseModal(true);
        }
      })
      .catch((error) => {
        setFormLoading(false);
        if (error.message !== "canceled") {
          console.error("Error saving category:", error);
          message.error("Failed to save category");
        }
      });
  };

  const handleFormClose = () => {
    setFormFields(initialFormValues);
    setUpdateId(null);
    setShouldCloseModal(false);
  };

  const handleReset = () => {
    setSearchFields(initialSearchValues);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(initialSearchValues, 1, pagination.pageSize);
  };

  const handlePagination = (paginationInfo, filters, sorter) => {
    const { current, pageSize } = paginationInfo;
    setPagination((prev) => ({
      ...prev,
      current: current,
      pageSize: pageSize,
    }));
    fetchData(searchFields, current, pageSize);
  };

  const searchPanel = (
    <Fragment>
      <FormTextField
        colSpan={4}
        label="Search"
        name="SearchText"
        size={INPUT_SIZE}
        value={searchFields.SearchText}
        onChange={handleSearchChange}
      />
    </Fragment>
  );

  const formPanel = (
    <Fragment>
      <FormTextField
        colSpan={8}
        label="Category Name"
        name="CategoryName"
        size={INPUT_SIZE}
        value={formFields.CategoryName}
        onChange={handleFormChange}
        required={true}
      />
      <FormSelect
        colSpan={8}
        listItem={supportingTable?.AdditionalData?.Departments || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={formFields.DepartmentId}
        onChange={handleFormChange}
      />
      <div className="ant-col ant-col-8 mt-26">
        <FormCheckbox
          name="IsEnable"
          label="Is Enabled"
          checked={formFields.IsEnable}
          onChange={handleFormChange}
        />
      </div>
    </Fragment>
  );

  return (
    <BasicModalFormComponent
      formTitle="Inventory Category"
      searchPanel={searchPanel}
      formPanel={formPanel}
      searchSubmit={handleSearchSubmit}
      onFormSave={handleFormSubmit}
      onFormClose={handleFormClose}
      tableRows={itemList}
      tableLoading={tableLoading}
      formLoading={formLoading}
      tableColumn={columns}
      deleteRow={handleDeleteRow}
      actionID="CategoryId"
      editRow={setUpdateId}
      fields={initialFormValues}
      reset={false}
      handleReset={handleReset}
      crudTitle="Category"
      externalCloseTrigger={shouldCloseModal}
      onPaginationChange={handlePagination}
      paginationConfig={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        pageSizeOptions: ['10', '20', '50', '100'],
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      }}
    />
  );
};

export default InventoryCategory;
